import React, { useMemo } from 'react';
import moment from 'moment';
import { useGraphData } from '../../hooks/useMeters';
import { buildGraphConfig } from '../../services/waterGraphConfig';
import { useGraphFilters, useDebouncedValue } from '../../hooks/useGraphFilters';
import { TIME_RANGES } from '../../utils/financialYearWindow';
import MeterChart from '../details/MeterChart';
import { useInsights } from '../../../../../globalComponents/insights/useInsights';
import InsightsPanel from '../../../../../globalComponents/insights/InsightsPanel';
import { INSIGHTS_TAB } from './waterCharts';

/**
 * One chart on the Water Management Dashboard.
 *
 * The page held four of these: a general one and three near-copies that
 * differed only in the arithmetic they ran over the response. The arithmetic
 * is a `derive` function here, so there is one card, one filter block and one
 * request path — the shape that a find-and-replace across the old file could
 * silently break in one copy and not the others.
 */

/**
 * Chart height.
 *
 * The x-axis band has to be taller than its ticks alone need, because the axis
 * title sits under them. Taking that from the chart would shorten every plot
 * on the page, so it comes from the slack `.graph-container` already reserves
 * instead, and the plot area is left as it was.
 */
const CHART_HEIGHT = 346;

/** Aggregations the control offers, in the order it offers them. */
const AGGREGATIONS = [
  { value: 'minute', label: 'Per Minute' },
  { value: 'hour', label: 'Hourly' },
  { value: 'day', label: 'Daily' },
  { value: 'month', label: 'Monthly' },
];

/**
 * Reads the rows out of the widget response, whichever shape it arrives in.
 * @param {object} payload - Response from the graph endpoint.
 * @returns {Array<object>} The rows, empty when there are none.
 */
const rowsOf = (payload) => {
  const rows = payload?.data?.data ?? payload?.data ?? [];
  return Array.isArray(rows) ? rows : [];
};

/**
 * @param {string} title - Card heading, as the page words it today.
 * @param {string} formula - The calculation, for a card whose figure is derived.
 * @param {string} currentFY - Financial year every request is scoped to.
 * @param {string|number} locationId - Location filter from the page header.
 * @param {string|number} subLocationId - Sub-location filter from the page header.
 * @param {string} module - Widget module, e.g. 'Water'.
 * @param {string} category - Widget category.
 * @param {string} subCategory - Widget sub-category.
 * @param {string|Array<string>} kpi - KPI or KPIs to request.
 * @param {string} stackByField - Field that separates the series, when stacked.
 * @param {Function} derive - Turns the response into the rows to plot.
 * @param {string} graphType - periodTotal for one series, seriesComparison for several.
 * @param {string} unit - Unit shown on the axis and in the tooltip.
 * @param {string} initialRange - Range to open on.
 * @param {string} initialAggregation - Aggregation to open on.
 * @param {string} chartId - Stable id of this chart, which keys its insights.
 * @param {boolean} showInsights - Whether the page's insights toggle is on.
 * @param {Function} onInsightsReady - Told what this chart's narrative says, so
 *   the page can summarise every chart at once.
 */
const WaterGraphCard = ({
  title,
  formula,
  currentFY,
  locationId,
  subLocationId,
  module = 'Water',
  category,
  subCategory,
  kpi,
  stackByField = null,
  derive,
  graphType = 'periodTotal',
  unit = 'm³',
  initialRange = 'week',
  initialAggregation = 'day',
  chartId,
  showInsights = false,
  onInsightsReady,
}) => {
  const {
    timeRange,
    aggregation,
    customStart,
    customEnd,
    dateRange,
    selectTimeRange,
    selectAggregation,
    setCustomStart,
    setCustomEnd,
  } = useGraphFilters({
    financialYear: currentFY,
    initialRange,
    initialAggregation,
  });

  const graphConfig = useMemo(
    () =>
      buildGraphConfig({
        title,
        financialYear: currentFY,
        window: dateRange,
        locationId,
        subLocationId,
        module,
        category,
        subCategory,
        kpi,
        stackByField,
        aggregation,
      }),
    [
      currentFY, dateRange, locationId, subLocationId, title, module,
      category, subCategory, kpi, aggregation, stackByField,
    ]
  );

  const settledConfig = useDebouncedValue(graphConfig);

  const { data, isLoading } = useGraphData(settledConfig, {
    enabled: !!settledConfig,
  });

  const rows = useMemo(() => {
    const raw = rowsOf(data);
    return derive ? derive(raw) : raw;
  }, [data, derive]);

  /**
   * The rows the chart draws, not the rows the request returned.
   *
   * Three of these charts compute what they plot from several KPIs. Sending
   * the response instead would have the narrative describe figures that are
   * nowhere on the chart the reader is looking at.
   */
  const graphData = useMemo(() => ({ data: rows }), [rows]);

  /**
   * Nothing is asked for until the chart has something to be asked about.
   *
   * An in-flight request leaves `rows` empty, which is indistinguishable from a
   * chart that reported nothing — so without this every load, and every change
   * of range, would narrate the empty state first and the data second.
   */
  const insightsEnabled = showInsights && !!settledConfig && !isLoading;

  const {
    insights,
    loading: insightsLoading,
    error: insightsError,
    refresh: refreshInsights,
  } = useInsights(
    INSIGHTS_TAB,
    chartId,
    graphData,
    settledConfig,
    insightsEnabled,
    onInsightsReady
  );

  return (
    <div className="graph-card wg-card">
      <div className="wg-head">
        <h3 className="wg-title">{title}</h3>
        {formula && <p className="wg-formula">{formula}</p>}
      </div>

      <div className="wg-controls">
        <div className="wg-field">
          <label className="wg-label" htmlFor={`${title}-range`}>Time Range</label>
          <select
            id={`${title}-range`}
            className="wg-select"
            value={timeRange}
            onChange={(event) => selectTimeRange(event.target.value)}
          >
            {TIME_RANGES.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="wg-field">
          <label className="wg-label" htmlFor={`${title}-agg`}>Aggregation</label>
          <select
            id={`${title}-agg`}
            className="wg-select"
            value={aggregation}
            onChange={(event) => selectAggregation(event.target.value)}
          >
            {AGGREGATIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {timeRange === 'custom' && (
          <>
            <div className="wg-field">
              <label className="wg-label" htmlFor={`${title}-from`}>Start Date</label>
              <input
                id={`${title}-from`}
                className="wg-select"
                type="datetime-local"
                value={customStart ? customStart.format('YYYY-MM-DDTHH:mm') : ''}
                onChange={(event) => setCustomStart(moment(event.target.value))}
              />
            </div>
            <div className="wg-field">
              <label className="wg-label" htmlFor={`${title}-to`}>End Date</label>
              <input
                id={`${title}-to`}
                className="wg-select"
                type="datetime-local"
                value={customEnd ? customEnd.format('YYYY-MM-DDTHH:mm') : ''}
                onChange={(event) => setCustomEnd(moment(event.target.value))}
              />
            </div>
          </>
        )}
      </div>

      <div className="graph-container">
        {isLoading ? (
          <div className="wg-skeleton" aria-label={`Loading ${title}`} />
        ) : (
          <MeterChart
            rows={rows}
            graphType={graphType}
            stackByField={stackByField}
            unit={unit}
            aggregation={aggregation}
            height={CHART_HEIGHT}
          />
        )}
      </div>

      {showInsights && (
        <div className="im-insights-panel">
          <InsightsPanel
            insights={insights}
            loading={insightsLoading}
            error={insightsError}
            onRefresh={refreshInsights}
          />
        </div>
      )}
    </div>
  );
};

export default WaterGraphCard;
